import { Moment } from '@chipinside/frontend'
import { Translate } from '@chipinside/translate'
import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { isNil } from 'lodash'

import { HeatStress } from '#/@types/backend'
import BooleanBadge from '#/components/badges/BooleanBadge'
import TypeDTR from '#/components/FormatCell/Types/dtr'
import { HeatStrength, TypeHeatStrength } from '#/components/FormatCell/Types/heatStrength'
import Fonts from '#/styles/Fonts'
import Palette from '#/styles/Palette'

export type HeatCycle = {
  'heat_cycles.heat.timestamp': string | null
  'heat_cycles.heat.heat_strength': HeatStrength | null
  'reproduction_method_id': string | null
  'heat_cycles.reproduction_method.timestamp': string | null
  'heat_cycles.reproduction_method.service_type': { status: string; text: string } | null
  'heat_cycles.reproduction_method.dtr': { status: HeatStress; text: string } | null
  'difference': number | null
  'has_data': boolean | null
  'got_pregnant': boolean | null
}

type TypeHeatCyclesProps = {
  value: HeatCycle[]
}

const headers = [
  'heat_strength',
  'detected_at',
  'service',
  'variation_short',
  'DTR_initials',
  'data_plural',
  'pregnant',
] as const

const Card = styled('div')({
  backgroundColor: Palette.gray.self[50],
  border: `1px solid ${Palette.gray.self[300]}`,
  borderRadius: 12,
  padding: '4px 16px',
})

const HeaderCell = styled(TableCell)({
  color: '#666',
  fontSize: Fonts.fontSize.SS,
  fontWeight: Fonts.fontWeight.medium,
  textTransform: 'capitalize',
  whiteSpace: 'nowrap',
})

const Cell = styled(TableCell)({
  fontSize: Fonts.fontSize.S,
  whiteSpace: 'nowrap',
})

const TypeHeatCycles = ({ value }: TypeHeatCyclesProps) => {
  if (!value?.length) return <span>-</span>

  return (
    <TableContainer component={Card} sx={{ overflow: 'hidden', position: 'relative' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers.map(header => (
              <HeaderCell
                key={header}
                sx={
                  ['heat_strength', 'detected_at', 'service', 'DTR_initials'].includes(header)
                    ? { width: 180 }
                    : undefined
                }
              >
                {Translate({ messageKey: header })}
              </HeaderCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {value.map((heatCycle, index) => {
            const heatTimestamp = heatCycle['heat_cycles.heat.timestamp']
            const heatStrength = heatCycle['heat_cycles.heat.heat_strength']
            const dtr = heatCycle['heat_cycles.reproduction_method.dtr']
            const serviceDate = heatCycle['heat_cycles.reproduction_method.timestamp']
            const serviceType = heatCycle['heat_cycles.reproduction_method.service_type']

            return (
              <TableRow
                hover
                key={`${heatTimestamp}-${index}`}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <Cell sx={{ width: 200 }}>
                  <TypeHeatStrength value={heatStrength ?? undefined} orientation="row" />
                </Cell>
                <Cell sx={{ width: 200 }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {heatTimestamp ? Moment(heatTimestamp).format('L LT') : '-'}
                  </Typography>
                </Cell>
                <Cell>
                  {serviceDate ? (
                    <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 1 }}>
                      <Typography>{Moment(serviceDate).format('L LT')}</Typography>
                      {serviceType?.text ? (
                        <Chip label={serviceType?.text} variant="outlined" size="small" />
                      ) : null}
                    </Stack>
                  ) : (
                    '-'
                  )}
                </Cell>
                <Cell>
                  <Typography>
                    {isNil(heatCycle.difference) ? '-' : heatCycle.difference}
                  </Typography>
                </Cell>
                <Cell sx={{ width: 200 }}>
                  <TypeDTR value={dtr ?? undefined} flexDirection="row" />
                </Cell>
                <Cell>
                  <BooleanBadge value={heatCycle.has_data} />
                </Cell>
                <Cell>
                  <BooleanBadge value={heatCycle.got_pregnant} />
                </Cell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default TypeHeatCycles
